import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Pencil, TestTube, Trash2, AlertTriangle, CheckCircle2, XCircle, Loader2, Settings2, Upload, Shield, ArrowDownToLine, Sparkles, ClipboardCheck, ScanSearch } from 'lucide-react';
import { ConnectorsEmpty } from '@/components/illustrations/ConnectorsEmpty';
import { useConnectors, useTestConnector, useDeleteConnector } from '@/hooks/useConnectors';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatRelativeDate, formatNumber } from '@/lib/formatters';
import { ConnectorDialog } from './ConnectorDialog';
import { SearchConfigDialog } from './SearchConfigDialog';
import { IngestionConfigDialog } from './IngestionConfigDialog';
import { BindingDialog } from './BindingDialog';
import { RegisterVectorsDialog } from './RegisterVectorsDialog';
import { SuggestionReviewDialog } from '@/pages/SuggestionReviewDialog';
import { getConnectorPreflight } from '@/api/connectors';
import type { ConnectorType, VectorDBConnector, TestConnectorResponse, PreflightResult } from '@/types/connector';

// Item C: connector types with a `list_vector_ids` lister adapter
// (`LISTERS` in connector_adapters.py). Gate the button to these so it never
// 422s on a connector type retroactive registration does not support.
// Must match the backend LISTERS dict (connector_adapters.py): the connector
// types whose adapter can enumerate vector ids for retroactive registration.
const RETROACTIVE_REGISTER_TYPES = new Set<ConnectorType>([
  'pgvector',
  'supabase',
  'neon',
  'qdrant',
  'pinecone',
  'azure_ai_search',
  'vertex_ai_search',
]);

const connectorLabels: Record<ConnectorType, string> = {
  pgvector: 'pgvector',
  pinecone: 'Pinecone',
  opensearch: 'OpenSearch',
  supabase: 'Supabase',
  neon: 'Neon',
  weaviate: 'Weaviate',
  qdrant: 'Qdrant',
  milvus: 'Milvus',
  chroma: 'Chroma',
  azure_ai_search: 'Azure AI Search',
  vertex_ai_vector_search: 'Vertex AI Vector Search',
  vertex_ai_search: 'Vertex AI Search',
};

const CONNECTOR_MATURITY: Record<string, 'GA' | 'Beta' | 'Preview'> = {
  pgvector: 'GA', supabase: 'GA', neon: 'GA', pinecone: 'GA', qdrant: 'GA',
  weaviate: 'Beta', opensearch: 'Beta', azure_ai_search: 'Beta',
  milvus: 'Preview', chroma: 'Preview',
  vertex_ai_vector_search: 'Preview', vertex_ai_search: 'Preview',
};

const MATURITY_STYLES: Record<'GA' | 'Beta' | 'Preview', string> = {
  GA: 'text-green-600 border-green-300',
  Beta: 'text-blue-600 border-blue-300',
  Preview: 'text-amber-600 border-amber-300',
};

const MAX_DISPLAYED_RESOURCES = 10;

function HealthBadge({ status }: { status: string }) {
  if (status === 'ok') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-status-allow bg-status-allow/10 rounded-full px-2 py-0.5">
        <CheckCircle2 className="h-3 w-3" />Healthy
      </span>
    );
  }
  if (status === 'degraded') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-status-warning bg-status-warning/10 rounded-full px-2 py-0.5">
        <AlertTriangle className="h-3 w-3" />Degraded
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-status-deny bg-status-deny/10 rounded-full px-2 py-0.5">
      <XCircle className="h-3 w-3" />Failed
    </span>
  );
}

const READINESS_CONFIG: Record<0 | 1 | 2 | 3 | 4, { label: string; color: string; tooltip: string; next: string }> = {
  0: {
    label: 'Not Ready',
    color: 'text-muted-foreground bg-muted/40',
    tooltip: 'Connector not reachable.',
    next: 'Test connection to reach L1.',
  },
  1: {
    label: 'Connection Ready',
    color: 'text-status-warning bg-status-warning/10',
    tooltip: 'Database reachable, auth valid.',
    next: 'Configure search to reach L2.',
  },
  2: {
    label: 'Search Ready',
    color: 'text-status-info bg-status-info/10',
    tooltip: 'Can search, coarse controls only.',
    next: 'Add active policies + resource-level bindings to reach L3.',
  },
  3: {
    label: 'Resource Policy',
    color: 'text-primary bg-primary/10',
    tooltip: 'Resource-level enforcement active.',
    next: 'Use inline or SQL view metadata for chunk-level enforcement (L4).',
  },
  4: {
    label: 'Chunk Policy',
    color: 'text-status-allow bg-status-allow/10',
    tooltip: 'Full chunk-level enforcement.',
    next: '',
  },
};

function PolicyReadinessBadge({ level, coveragePct }: { level: 0 | 1 | 2 | 3 | 4; coveragePct: number | null }) {
  const config = READINESS_CONFIG[level];
  return (
    <div className="group relative inline-flex">
      <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 ${config.color}`}>
        <Shield className="h-3 w-3" />{config.label}
        {coveragePct != null && level >= 2 && <span className="ml-0.5 opacity-75">({coveragePct}% bound)</span>}
      </span>
      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-popover text-popover-foreground text-xs rounded px-2 py-1 shadow whitespace-nowrap border z-10 max-w-xs">
        <p className="font-medium">L{level}: {config.label}</p>
        <p>{config.tooltip}</p>
        {config.next && <p className="text-muted-foreground mt-0.5">{config.next}</p>}
      </div>
    </div>
  );
}

function CoverageBar({ coveragePct }: { coveragePct: number | null }) {
  const pct = coveragePct ?? 0;
  return (
    <div className="group relative">
      <div className="w-full bg-muted rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full transition-all ${
            pct === 0 ? 'bg-muted' : pct < 80 ? 'bg-status-warning' : 'bg-status-allow'
          }`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-popover text-popover-foreground text-xs rounded px-2 py-1 shadow whitespace-nowrap border">
        Estimated metadata binding coverage
      </div>
    </div>
  );
}

function TestResultPanel({ result }: { result: TestConnectorResponse }) {
  const [showAll, setShowAll] = useState(false);
  const resources = result.resources ?? [];
  const displayedResources = showAll ? resources : resources.slice(0, MAX_DISPLAYED_RESOURCES);
  const hiddenCount = resources.length - MAX_DISPLAYED_RESOURCES;

  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-center justify-between">
        <HealthBadge status={result.health_status} />
        <span className="text-muted-foreground">{result.latency_ms}ms</span>
      </div>

      {result.server_version && (
        <p className="text-muted-foreground">Server: {result.server_version}</p>
      )}

      {result.error && (
        <p className="text-destructive bg-destructive/10 rounded p-2">{result.error}</p>
      )}

      {resources.length > 0 && (
        <div className="space-y-1">
          <p className="font-medium text-muted-foreground">
            {resources.length} {result.resource_kind}{resources.length !== 1 ? 's' : ''} discovered
            {result.approximate_counts && <span className="ml-1 italic">(approx. counts)</span>}
          </p>
          <div className="space-y-0.5">
            {displayedResources.map((r) => (
              <div key={r.name} className="flex items-center justify-between rounded bg-muted/50 px-2 py-1">
                <span className="font-mono truncate max-w-[60%]">{r.name}</span>
                <span className="text-muted-foreground whitespace-nowrap ml-2">
                  {r.record_count != null ? formatNumber(r.record_count) + ' records' : '—'}
                  {r.dimension != null && <span className="ml-1">({r.dimension}d{r.metric ? `, ${r.metric}` : ''})</span>}
                </span>
              </div>
            ))}
          </div>
          {!showAll && hiddenCount > 0 && (
            <button
              onClick={() => setShowAll(true)}
              className="text-primary hover:underline text-xs"
            >
              +{hiddenCount} more
            </button>
          )}
        </div>
      )}

      {result.warnings.length > 0 && (
        <div className="rounded bg-status-warning/10 p-2 text-status-warning space-y-0.5">
          {result.warnings.map((w, i) => (
            <p key={i} className="flex items-start gap-1">
              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />{w}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function PreflightCheckRow({ check }: { check: { name: string; passed: boolean; blocking: boolean; message: string } }) {
  return (
    <div className={`flex items-start gap-3 rounded-md px-3 py-2.5 text-sm ${check.passed ? 'bg-status-allow/5' : check.blocking ? 'bg-status-deny/5' : 'bg-status-warning/5'}`}>
      {check.passed ? (
        <CheckCircle2 className="h-4 w-4 text-status-allow mt-0.5 shrink-0" />
      ) : check.blocking ? (
        <XCircle className="h-4 w-4 text-status-deny mt-0.5 shrink-0" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-status-warning mt-0.5 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-xs leading-tight">
          {check.name}
          {!check.passed && check.blocking && (
            <span className="ml-1.5 text-xs font-normal text-status-deny">(blocking)</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">{check.message}</p>
      </div>
    </div>
  );
}

function PreflightDialog({ connectorName, result, onClose }: { connectorName: string; result: PreflightResult; onClose: () => void }) {
  const blockingFailed = result.checks.filter((c) => !c.passed && c.blocking).length;
  const nonBlockingFailed = result.checks.filter((c) => !c.passed && !c.blocking).length;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Preflight Check: {connectorName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Banner */}
          <div className={`rounded-lg px-4 py-3 ${result.ready_for_production ? 'bg-status-allow/10' : 'bg-status-deny/10'}`}>
            <div className="flex items-center gap-2">
              {result.ready_for_production ? (
                <CheckCircle2 className="h-5 w-5 text-status-allow shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 text-status-deny shrink-0" />
              )}
              <p className={`font-semibold text-sm ${result.ready_for_production ? 'text-status-allow' : 'text-status-deny'}`}>
                {result.ready_for_production
                  ? 'Ready for production'
                  : blockingFailed === 1
                    ? 'Not ready — 1 blocking check failed'
                    : `Not ready — ${blockingFailed} blocking checks failed`}
              </p>
            </div>
            {nonBlockingFailed > 0 && !result.ready_for_production && (
              <p className="text-xs text-muted-foreground mt-1 ml-7">
                {nonBlockingFailed} non-blocking {nonBlockingFailed === 1 ? 'issue' : 'issues'} also found.
              </p>
            )}
          </div>

          {/* Recommendation */}
          {result.recommendation && (
            <div className="rounded-md border border-primary/20 bg-primary/5 px-4 py-3">
              <p className="text-xs font-medium text-primary mb-0.5">Recommendation</p>
              <p className="text-sm">{result.recommendation}</p>
            </div>
          )}

          {/* Checks list */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Checks ({result.checks.length})
            </p>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {result.checks.map((check, i) => (
                <PreflightCheckRow key={i} check={check} />
              ))}
            </div>
          </div>

          {/* Docs link */}
          <p className="text-xs text-muted-foreground">
            Need help?{' '}
            <a
              href="/docs/production-deployment"
              className="text-primary underline underline-offset-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              View production deployment guide
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreflightButton({ connector }: { connector: VectorDBConnector }) {
  const [open, setOpen] = useState(false);

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['preflight', connector.id],
    queryFn: () => getConnectorPreflight(connector.id),
    enabled: false,
    staleTime: 60_000,
  });

  async function handleClick() {
    await refetch();
    setOpen(true);
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={isFetching}
        title="Run preflight readiness check"
      >
        {isFetching ? (
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
        ) : (
          <ClipboardCheck className="h-3 w-3 mr-1" />
        )}
        Preflight
      </Button>

      {isError && !isFetching && (
        <span className="text-xs text-destructive self-center">Preflight failed</span>
      )}

      {open && data && (
        <PreflightDialog
          connectorName={connector.name}
          result={data}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function ConnectorsPage() {
  const { data: connectors, isLoading } = useConnectors();
  const testMutation = useTestConnector();
  const deleteMutation = useDeleteConnector();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, TestConnectorResponse | null>>({});
  const [testingId, setTestingId] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingConnector, setEditingConnector] = useState<VectorDBConnector | undefined>();
  const [searchConfigConnector, setSearchConfigConnector] = useState<VectorDBConnector | null>(null);
  const [ingestionConfigConnector, setIngestionConfigConnector] = useState<VectorDBConnector | null>(null);
  const [bindingConnector, setBindingConnector] = useState<VectorDBConnector | null>(null);
  const [registerVectorsConnector, setRegisterVectorsConnector] = useState<VectorDBConnector | null>(null);
  const [suggestionConnector, setSuggestionConnector] = useState<VectorDBConnector | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  function openCreate() {
    setEditingConnector(undefined);
    setShowDialog(true);
  }

  function openEdit(conn: VectorDBConnector) {
    setEditingConnector(conn);
    setShowDialog(true);
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  const filtered = (connectors ?? []).filter(c =>
    !searchQuery ||
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        eyebrow="Data Sources"
        title="Vector DB Connectors"
        subtitle="Connect your vector databases to gate access to stored embeddings."
        actions={<Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Add Connector</Button>}
      />

      {connectors && connectors.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: connectors.length },
            { label: 'Connected', value: connectors.filter(c => c.status === 'connected').length },
            { label: 'Search Ready', value: connectors.filter(c => c.search_ready).length },
            { label: 'Errors', value: connectors.filter(c => c.status === 'error').length },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-lg border bg-card px-4 py-3">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-2xl font-semibold tabular mt-0.5">{value}</p>
            </div>
          ))}
        </div>
      )}

      {connectors && connectors.length > 0 && (
        <div className="flex items-center gap-3">
          <Input
            placeholder="Filter connectors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
        </div>
      )}

      {!connectors || connectors.length === 0 ? (
        <EmptyState
          icon={<ConnectorsEmpty className="h-28 w-28 opacity-80" />}
          title="No connectors configured"
          description="Add a vector database connector to start protecting your AI retrieval pipeline."
          action={<Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Add Connector</Button>}
        />
      ) : filtered.length === 0 && searchQuery ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No connectors match &ldquo;{searchQuery}&rdquo;.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((conn) => {
            const activeResult = testResult[conn.id] ?? conn.diagnostics;
            const isUntested = !conn.last_tested_at && !conn.connection_ready;
            return (
              <div key={conn.id} className="rounded-lg border bg-card p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider flex items-center gap-1.5">
                      {connectorLabels[conn.type] ?? conn.type}
                      {CONNECTOR_MATURITY[conn.type] && (
                        <span className={`text-xs border rounded px-1.5 py-0.5 font-medium normal-case tracking-normal ${MATURITY_STYLES[CONNECTOR_MATURITY[conn.type]]}`}>
                          {CONNECTOR_MATURITY[conn.type]}
                        </span>
                      )}
                    </p>
                    <h3 className="font-semibold mt-1">{conn.name}</h3>
                  </div>
                  <StatusBadge status={conn.status} />
                </div>

                {/* Three-signal status row */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {conn.connection_ready && !conn.search_ready && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-status-warning bg-status-warning/10 rounded-full px-2 py-0.5">
                      <AlertTriangle className="h-3 w-3" />No search config
                    </span>
                  )}
                  {conn.search_ready && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-status-allow bg-status-allow/10 rounded-full px-2 py-0.5">
                      <CheckCircle2 className="h-3 w-3" />Search ready
                    </span>
                  )}
                  {conn.ingestion_capable && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-status-info bg-status-info/10 rounded-full px-2 py-0.5">
                      <ArrowDownToLine className="h-3 w-3" />Ingestion capable
                    </span>
                  )}
                  {conn.ingestion_ready && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 rounded-full px-2 py-0.5">
                      <CheckCircle2 className="h-3 w-3" />Write configured
                    </span>
                  )}
                  <PolicyReadinessBadge level={conn.policy_readiness_level} coveragePct={conn.coverage_pct} />
                </div>

                <CoverageBar coveragePct={conn.coverage_pct} />

                {isUntested && (
                  <p className="text-xs font-medium text-status-warning">
                    Not verified yet. Click Test to check the connection.
                  </p>
                )}

                {conn.connection_ready && !conn.search_ready && (
                  <p className="text-xs text-status-warning">
                    Connected, but not configured for retrieval.
                  </p>
                )}

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Indexes</p>
                    <p className="font-medium">{conn.index_count}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Records</p>
                    <p className="font-medium">{formatNumber(conn.record_count)}</p>
                  </div>
                </div>

                {conn.last_sync && (
                  <p className="text-xs text-muted-foreground">Last sync: {formatRelativeDate(conn.last_sync)}</p>
                )}

                {conn.last_tested_at && (
                  <p className="text-xs text-muted-foreground">Last tested: {formatRelativeDate(conn.last_tested_at)}</p>
                )}

                {conn.server_version && !activeResult && (
                  <p className="text-xs text-muted-foreground">Server: {conn.server_version}</p>
                )}

                {conn.error_message && !activeResult && (
                  <p className="text-xs text-destructive bg-destructive/10 rounded p-2">{conn.error_message}</p>
                )}

                {activeResult && <TestResultPanel result={activeResult} />}

                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={() => openEdit(conn)}>
                    <Pencil className="h-3 w-3 mr-1" />Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSearchConfigConnector(conn)}>
                    <Settings2 className="h-3 w-3 mr-1" />{conn.search_ready ? 'Search Config' : 'Configure Search'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setBindingConnector(conn)}>
                    <Upload className="h-3 w-3 mr-1" />Bind Metadata
                  </Button>
                  {RETROACTIVE_REGISTER_TYPES.has(conn.type) && (
                    <Button variant="outline" size="sm" onClick={() => setRegisterVectorsConnector(conn)}>
                      <ScanSearch className="h-3 w-3 mr-1" />Register Existing Vectors
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setSuggestionConnector(conn)}>
                    <Sparkles className="h-3 w-3 mr-1" />Suggest Classifications
                  </Button>
                  <PreflightButton connector={conn} />
                  {conn.ingestion_capable && (
                    <Button variant="outline" size="sm" onClick={() => setIngestionConfigConnector(conn)}>
                      <ArrowDownToLine className="h-3 w-3 mr-1" />{conn.ingestion_ready ? 'Write Config' : 'Configure Write'}
                    </Button>
                  )}
                  <Button
                    variant={isUntested ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setTestingId(conn.id);
                      testMutation.mutate(conn.id, {
                        onSuccess: (result) => {
                          setTestResult((prev) => ({ ...prev, [conn.id]: result }));
                          setTestingId(null);
                        },
                        onError: () => setTestingId(null),
                      });
                    }}
                    disabled={testingId === conn.id}
                  >
                    {testingId === conn.id ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <TestTube className="h-3 w-3 mr-1" />
                    )}
                    Test
                  </Button>
                  {confirmDelete === conn.id ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => { deleteMutation.mutate(conn.id); setConfirmDelete(null); }}
                    >
                      Confirm Delete
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(conn.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConnectorDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        mode={editingConnector ? 'edit' : 'create'}
        initialData={editingConnector}
      />

      {searchConfigConnector && (
        <SearchConfigDialog
          open={!!searchConfigConnector}
          onOpenChange={(open) => { if (!open) setSearchConfigConnector(null); }}
          connector={searchConfigConnector}
        />
      )}

      {ingestionConfigConnector && (
        <IngestionConfigDialog
          open={!!ingestionConfigConnector}
          onOpenChange={(open) => { if (!open) setIngestionConfigConnector(null); }}
          connector={ingestionConfigConnector}
        />
      )}

      {bindingConnector && (
        <BindingDialog
          open={!!bindingConnector}
          onOpenChange={(open) => { if (!open) setBindingConnector(null); }}
          connectorId={bindingConnector.id}
          connectorName={bindingConnector.name}
        />
      )}

      {registerVectorsConnector && (
        <RegisterVectorsDialog
          open={!!registerVectorsConnector}
          onOpenChange={(open) => { if (!open) setRegisterVectorsConnector(null); }}
          connectorId={registerVectorsConnector.id}
          connectorName={registerVectorsConnector.name}
        />
      )}

      {suggestionConnector && (
        <SuggestionReviewDialog
          open={!!suggestionConnector}
          onOpenChange={(open) => { if (!open) setSuggestionConnector(null); }}
          connectorId={suggestionConnector.id}
          connectorName={suggestionConnector.name}
        />
      )}
    </div>
  );
}
